import React from 'react';
import Reveal from '../lib/Reveal.jsx';

const THEMES = [
  { t: 'Developer productivity', d: 'Measuring and improving software delivery using AI-assisted and agentic workflows. Applied to banks modernising engineering organisations alongside core platform migrations.' },
  { t: 'Autonomous evaluation', d: 'Designing evaluation systems for agentic delivery and AI-generated outputs. Critical for banks deploying AI in credit, compliance, and operational workflows.' },
  { t: 'Knowledge graphs', d: 'Typed, source-grounded knowledge structures for enterprise AI. Directly applicable to product master data, regulatory reporting, and KYC/AML in financial institutions.' },
  { t: 'Digital twins', d: 'Modelling complex enterprise systems, software platforms, and operations. Applied to banking cores, payment networks, and compliance-critical infrastructure.' },
  { t: 'AI-assisted software engineering', d: 'Agents, context managers, and governed workflows in real codebases. Applied to banking engineering teams managing legacy and modern platform coexistence.' },
  { t: 'Enterprise AI governance', d: 'IP exposure, hallucination risk, unsafe automation, traceability gaps. Especially acute in financial services where model risk and regulatory accountability are non-negotiable.' },
  { t: 'Core banking transformation', d: 'AI-assisted modernisation of legacy banking cores: platform selection, migration risk, and organisational change for retail and commercial banks.' },
  { t: 'Financial infrastructure & payments', d: 'The modernisation layer between core banking, payment rails, and compliance — clearing, settlement, CBDC, and cross-border corridor design.' },
];

export default function Publications({ tweaks }) {
  return (
    <section id="writing" className="section">
      <div className="container">
        <div className="grid12" style={{ marginBottom: 64, alignItems: 'end' }}>
          <Reveal style={{ gridColumn: 'span 6' }}>
            <div className="section-marker" style={{ marginBottom: 24 }}>
              {tweaks.sectionDividers === 'numbered' ? '05 / Writing' : 'Writing'}
            </div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: 'clamp(36px, 5vw, 60px)', lineHeight: 1.05, letterSpacing: '-0.025em', margin: 0, textWrap: 'balance' }}>
              Notes on the <span style={{ fontStyle: 'italic', color: 'var(--clay)' }}>hard edge</span> of enterprise AI.
            </h2>
          </Reveal>
          <Reveal style={{ gridColumn: '8 / span 5' }}>
            <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink-60)', margin: 0, maxWidth: '42ch' }}>
              Not just how to use models — how to evaluate, govern, and deploy them inside financial institutions. How banks measure model risk, protect customer data, and integrate AI into operations that move billions of dollars a day.
            </p>
          </Reveal>
        </div>

        <Reveal style={{ marginBottom: 48 }}>
          <a href="/landing-zone?asset=sdlc" style={{ display: 'block', padding: '28px 32px', background: 'var(--clay-wash)', border: '1px solid var(--clay)', textDecoration: 'none', color: 'var(--ink)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 }}>
              <div>
                <div className="mono-label" style={{ color: 'var(--clay)', marginBottom: 12 }}>New · Engineering standard · Apr 2026 · JT-SDLC-001</div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: 'clamp(22px, 3vw, 34px)', lineHeight: 1.1, letterSpacing: '-0.02em', margin: '0 0 12px' }}>
                  Harness-neutral <em style={{ fontStyle: 'italic' }}>SDLC standard</em>
                </h3>
                <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--ink-60)', margin: 0, maxWidth: '54ch' }}>
                  A portable software development lifecycle that any agentic harness — Codex, Claude Code, Cursor, or a future one — can implement without rewriting the playbook. 14 pages, 16 sections, free preview of §1–2.
                </p>
              </div>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--clay)', flexShrink: 0, paddingTop: 4 }}>Read →</span>
            </div>
          </a>
        </Reveal>

        <Reveal stagger className="mobile-collapse-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0 }}>
          {THEMES.map((th, i) => (
            <div key={th.t} className="mobile-pub-row" style={{
              padding: '28px 28px 28px 0',
              borderTop: '1px solid var(--ink-12)',
              borderRight: (i % 2 === 0) ? '1px solid var(--ink-12)' : 'none',
              paddingLeft: (i % 2 === 1) ? 28 : 0,
              display: 'grid', gridTemplateColumns: '40px 1fr', gap: 16, alignItems: 'start',
            }}>
              <div className="metric" style={{ fontSize: 13, color: 'var(--ink-40)' }}>{String(i + 1).padStart(2, '0')}</div>
              <div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: 24, lineHeight: 1.25, letterSpacing: '-0.01em', margin: '0 0 8px' }}>{th.t}</h3>
                <p style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--ink-60)', margin: 0, maxWidth: '46ch' }}>{th.d}</p>
              </div>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}
